import API from './api';

export const getAllVideoPodcasts = async (page = 1, limit = 20, category = null, authorId = null, search = null) => {
  try {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('limit', limit);
    if (category) params.append('category', category);
    if (authorId) params.append('authorId', authorId);
    if (search) params.append('search', search);

    const response = await API.get(`/video-podcasts?${params.toString()}`);
    
    // Handle case where response might not have success field (empty result)
    if (response.data && response.data.success === false) {
      throw new Error(response.data.message || 'Failed to fetch video podcasts');
    }
    
    // If response doesn't have success field, assume it's successful if podcasts array exists
    if (response.data && !response.data.success && !response.data.podcasts) {
      // Return empty result instead of throwing error
      return {
        success: true,
        podcasts: [],
        pagination: {
          page: parseInt(page),
          limit: parseInt(limit),
          total: 0,
          pages: 0,
        },
      };
    }
    
    return response.data;
  } catch (error) {
    console.error('Error in getAllVideoPodcasts:', error);
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error?.message || 
                        error.message || 
                        'Failed to fetch video podcasts';
    throw new Error(errorMessage);
  }
};

export const getVideoPodcastById = async (id) => {
  try {
    if (!id) {
      throw new Error('Video podcast ID is required');
    }
    const response = await API.get(`/video-podcasts/${id}`);
    if (!response.data || !response.data.success) {
      throw new Error(response.data?.message || 'Failed to fetch video podcast');
    }
    if (!response.data.podcast) {
      throw new Error('Video podcast not found');
    }
    return response.data.podcast;
  } catch (error) {
    console.error('Error in getVideoPodcastById:', error);
    throw new Error(error.response?.data?.message || error.message || 'Failed to fetch video podcast');
  }
};

export const createVideoPodcast = async (formData, token) => {
  try {
    const response = await API.post('/video-podcasts', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to create video podcast');
    }
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to create video podcast');
  }
};

export const updateVideoPodcast = async (id, formData, token) => {
  try {
    const response = await API.put(`/video-podcasts/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update video podcast');
    }
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to update video podcast');
  }
};

export const deleteVideoPodcast = async (id, token) => {
  try {
    const response = await API.delete(`/video-podcasts/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete video podcast');
    }
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to delete video podcast');
  }
};

export const toggleLikeVideoPodcast = async (id, userId, token) => {
  try {
    const response = await API.post(
      `/video-podcasts/${id}/like`,
      { userId },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to toggle like');
    }
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to toggle like');
  }
};

export const getUserVideoPodcasts = async (userId, page = 1, limit = 20, publishedOnly = false, search = null) => {
  try {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('limit', limit);
    if (publishedOnly) params.append('publishedOnly', 'true');
    if (search) params.append('search', search);

    const response = await API.get(`/video-podcasts/user/${userId}?${params.toString()}`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch user video podcasts');
    }
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to fetch user video podcasts');
  }
};

export const getPublishedPodcastsByAuthor = async (authorId, page = 1, limit = 50) => {
  try {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('limit', limit);

    const response = await API.get(`/video-podcasts/author/${authorId}/published?${params.toString()}`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch published podcasts');
    }
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to fetch published podcasts');
  }
};

export const convertVideoToMP4 = async (podcastId, token) => {
  try {
    const response = await API.post(
      `/video-podcasts/${podcastId}/convert`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to convert video');
    }
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to convert video');
  }
};

export const getVideoPodcastComments = async (podcastId) => {
  try {
    const response = await API.get(`/video-podcasts/${podcastId}/comments`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch comments');
    }
    return response.data.comments || [];
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to fetch comments');
  }
};

export const addVideoPodcastComment = async (podcastId, text, userId, token) => {
  try {
    const response = await API.post(
      `/video-podcasts/${podcastId}/comments`,
      { text, userId },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to add comment');
    }
    return response.data.comment;
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to add comment');
  }
};

export const deleteVideoPodcastComment = async (podcastId, commentId, token) => {
  try {
    const response = await API.delete(
      `/video-podcasts/${podcastId}/comments/${commentId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete comment');
    }
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || error.message || 'Failed to delete comment');
  }
};

